import { computed } from 'vue';
import {
	PermissionRules,
	EXCHANGE_BRAND_ENTITY_TYPES,
	EXCHANGE_BRAND_USER_TYPES,
	ROLES,
	type PermissionRule,
	type Permission,
	type Role,
} from '@teamworksdev/influencer-core';
import { useUserStore } from '@/stores/userStore';
import { accessContentHub, accessContentHubDashboard, accessContentHubMyContent, readContentHub, hasContentHubWrite } from '@/permissions/contentHub';
import { accessBulletins, accessTransactions, canReportTransactions, hasExchangeWrite } from '@/permissions/exchange';
import { accessContentTasks, canCreateContentTask } from '@/permissions/contentTasks';

export function usePermissions() {
	const userStore = useUserStore();

	// Helpers
	const can = (rule: PermissionRule) => computed(() => userStore.checkUserPermission(rule));
	const cannot = (rule: PermissionRule) => computed(() => !userStore.checkUserPermission(rule));
	const canAny = (...rules: PermissionRule[]) => computed(() => rules.some((rule) => userStore.checkUserPermission(rule)));
	const canAll = (...rules: PermissionRule[]) => computed(() => rules.every((rule) => userStore.checkUserPermission(rule)));


	// Convenience methods
	const hasRole = (role: Role) => can(PermissionRules.hasRole(role));
	const hasTeamPermission = (permission: Permission) => can(PermissionRules.hasTeamPermission(permission));


	// Role checks
	const isAgentUser = hasRole(ROLES.AGENT);
	const isTeamUser = hasRole(ROLES.TEAM_USER);
	const isBrandUser = hasRole(ROLES.BRAND_USER);
	const isComplianceUser = hasRole(ROLES.COMPLIANCE_OFFICER);


	// Other checks
	const isBrandAdmin = can(PermissionRules.isBrandUserType(EXCHANGE_BRAND_USER_TYPES.EXCHANGE_ADMIN));
	const isBrandBusiness = can(PermissionRules.isBrandEntityType(EXCHANGE_BRAND_ENTITY_TYPES.BUSINESS));

	const hasContentHubReadAccess = can(readContentHub);
	const hasContentHubWriteAccess = can(hasContentHubWrite);
	const hasContentHubMyContentAccess = can(accessContentHubMyContent);
	const hasContentHubAccess = can(accessContentHub);
	const hasContentHubDashboardAccess = can(accessContentHubDashboard);

	const hasContentTasksAccess = can(accessContentTasks);
	const hasCreateContentTaskAccess = can(canCreateContentTask);

	const hasBulletinsAccess = can(accessBulletins);

	// Exchange checks
	const hasTransactionsAccess = can(accessTransactions);
	const hasReportTransactionsAccess = can(canReportTransactions);
	const hasExchangeWriteAccess = can(hasExchangeWrite);

	return {
		can,
		cannot,
		canAny,
		canAll,
		hasRole,
		hasTeamPermission,

		isAgentUser,
		isTeamUser,
		isBrandUser,
		isComplianceUser,

		isBrandAdmin,
		isBrandBusiness,

		hasContentHubReadAccess,
		hasContentHubWriteAccess,
		hasContentHubMyContentAccess,
		hasContentHubAccess,
		hasContentHubDashboardAccess,

		hasContentTasksAccess,
		hasCreateContentTaskAccess,

		hasBulletinsAccess,

		hasTransactionsAccess,
		hasReportTransactionsAccess,
		hasExchangeWriteAccess,
	};
}
